import { isAxiosError } from "axios";

import { del, pget, put } from "@/api";
import { post } from "@/api";
import { Models } from "@/api/types";
import { queryClient } from "@/pages/Root";
import { formatCurrency } from "@/toolbox";

import {
  CURRENT_ORDER_QUERY_KEY,
  invalidateCurrentOrderDetails,
} from "./useCartDetails";

export const MEMBERSHIP_BENEFIT_KEY = "membership_benefit";

const getPromotions = pget("/promotions");

export type ActionResult = Awaited<
  ReturnType<
    typeof applyPromoCode | typeof ondeletePromoCode | typeof updateLocation
  >
>;

export type Location = Models<"PlatformLocationModel">;

export interface LocationsResponse {
  items: Location[];
}

export type DeleteItemAction = {
  action: "deleteItem";
  shopDisplayOrderLineItemId: string;
};

export type EditItemAction = {
  action: "editItem";
  shopDisplayOrderLineItemId: string;
  packageId: string;
  packagePriceId: string;
  packageListingId: string;
};

export type PromoCodeAction = {
  action: "promoCodeAction";
  promoCode: string;
};

export type RewardAction = {
  action: "rewardAction";
  rewardId: string;
};

export type RemovePromoCodeAction = {
  action: "removePromoCodeAction";
  promoCodeId: string;
};

export type ApplyCashAction = { action: "applyCash"; amount: number };

export type SetSalesAssociateAction = {
  action: "setSalesAssociate";
  salesAssociateId: string;
};

export type DeleteSalesAssociateAction = {
  action: "deleteSalesAssociate";
};

export type UpdateLocationAction = {
  action: "updateLocation";
  locationId: string;
  shopDisplayOrderId: string;
  rewardId: string;
};

export type CartPageAction =
  | ApplyCashAction
  | DeleteItemAction
  | EditItemAction
  | PromoCodeAction
  | RemovePromoCodeAction
  | SetSalesAssociateAction
  | DeleteSalesAssociateAction
  | RewardAction
  | UpdateLocationAction;

export const applyRepeatCash = async (data: number) => {
  await put("/orders/current/repeatCash", {
    repeatCashInCents: data,
  });
  await invalidateCurrentOrderDetails();
  return true;
};

export const onEditCartItem = async (item: EditItemAction) => {
  await put("/orders/current/lineitems/{shopDisplayOrderLineItemId}", {
    shopDisplayOrderLineItemId: item.shopDisplayOrderLineItemId,
    packageId: item.packageId,
    packagePriceId: item.packagePriceId,
    packageListingId: item.packageListingId,
  });
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: ["/orders/current/available-promos"],
    }),
    invalidateCurrentOrderDetails(),
  ]);

  return true;
};

export const ondeleteCartItem = async (item: DeleteItemAction) => {
  await del("/orders/current/lineitems/{shopDisplayOrderLineItemId}", {
    shopDisplayOrderLineItemId: item.shopDisplayOrderLineItemId,
  });
  // First update cart-related queries
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: ["/orders/current/lineitems"],
    }),
    invalidateCurrentOrderDetails(),
  ]);

  // Then update promotion and location queries
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: ["/orders/current/available-promos"],
    }),
    queryClient.invalidateQueries({ queryKey: ["platform/locations"] }),
    queryClient.invalidateQueries({ queryKey: ["membership/details"] }),
  ]);

  return true;
};

export const discountAppliedMessage = (
  promoCode: Models<"ShopDisplayOrderDiscountSummaryLineItem"> | undefined,
  shopDisplayOrderLineItems: Models<"ShopDisplayOrderLineItemModel">[]
) => {
  if (!promoCode) return "";

  const {
    percentOff = 0,
    membershipsDiscountTotal = 0,
    amountOff = 0,
    shopDisplayOrderLineItemId,
  } = promoCode;

  if (percentOff === 1 && membershipsDiscountTotal > 0) {
    return "Free service";
  }

  const itemName =
    shopDisplayOrderLineItems.find(
      (item) => shopDisplayOrderLineItemId === item.id
    )?.name ?? "";

  if (percentOff) {
    return `${percentOff * 100}% off ${itemName} applied`;
  }

  return `${formatCurrency(amountOff)} off ${itemName} applied`;
};

export const applyPromoCode = async (data: {
  action: "promoCodeAction";
  promoCode: string;
}) => {
  try {
    const response = await post("/orders/current/promoCode", {
      // @ts-expect-error body type is marked as `object` in the API, but it actually expects a string
      $data: data.promoCode,
    });
    await invalidateCurrentOrderDetails();

    const promoCodeDiscount = response.discountSummary?.discounts?.find(
      (discount) => discount.promoCode != null
    );
    const coupon = response.discountSummary?.discounts?.find(
      (discount) => discount.promoCode
    )?.promoCode;
    return {
      success: discountAppliedMessage(
        promoCodeDiscount,
        response.shopDisplayOrderLineItems || []
      ),
      discountId: promoCodeDiscount?.discountId,
      action: "promoCodeAction" as const,
      coupon: coupon,
    };
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 400) {
      if (
        typeof error.response.data === "string" &&
        error.response.data === "itemsInCartNotIncludedInDiscount"
      ) {
        return {
          error: "Please add an included item to your cart",
          action: "promoCodeAction" as const,
        };
      }
    }

    return {
      error: "Invalid Code. Please enter a valid code",
      action: "promoCodeAction" as const,
    };
  }
};

export const ondeletePromoCode = async (item: RemovePromoCodeAction) => {
  try {
    await del("/orders/current/promoCode/{promoCodeId}", {
      promoCodeId: item.promoCodeId,
    });
    await Promise.all([
      invalidateCurrentOrderDetails(),
      queryClient.invalidateQueries({
        queryKey: ["/orders/current/available-promos"],
      }),
      queryClient.invalidateQueries({
        queryKey: ["membership/details"],
      }),
      queryClient.invalidateQueries(getPromotions.keyed()),
    ]);
    return { deleted: true, action: "deletePromoCode" as const };
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 400) {
      if (
        typeof error.response.data === "string" &&
        error.response.data === "itemsInCartNotIncludedInDiscount"
      ) {
        return {
          error: "Please add an included item to your cart",
          action: "deletePromoCode" as const,
        };
      }
    }
    return {
      error: "Not able to remove promo code from cart. try again later",
      action: "deletePromoCode" as const,
    };
  }
};

export const setSalesAssociate = async (data: SetSalesAssociateAction) => {
  try {
    await post("/orders/current/sales-associate", {
      $data: data.salesAssociateId,
    });
    await invalidateCurrentOrderDetails();
    return {
      action: "setSalesAssociate" as const,
    };
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 400) {
      if (
        typeof error.response.data === "string" &&
        error.response.data === "itemsInCartNotIncludedInDiscount"
      ) {
        return {
          error: "Please add an included item to your cart",
          action: "setSalesAssociate" as const,
        };
      }
    }
    return {
      action: "setSalesAssociate" as const,
    };
  }
};

export const deleteSalesAssociate = async () => {
  try {
    await Promise.all([
      del("/orders/current/sales-associate", undefined),
      invalidateCurrentOrderDetails(),
    ]);

    return {
      action: "deleteSalesAssociate" as const,
    };
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 400) {
      if (
        typeof error.response.data === "string" &&
        error.response.data === "itemsInCartNotIncludedInDiscount"
      ) {
        return {
          error: "Please add an included item to your cart",
          action: "deleteSalesAssociate" as const,
        };
      }
    }
    return {
      action: "deleteSalesAssociate" as const,
    };
  }
};

export const applyReward = async (data: {
  action: "rewardAction";
  rewardId: string;
}) => {
  try {
    await Promise.all([
      post("/rewards/redeem", {
        rewardId: data.rewardId,
        referralRewardId: null,
      }),
      invalidateCurrentOrderDetails(),
    ]);

    return {
      action: "rewardAction" as const,
    };
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 400) {
      if (
        typeof error.response.data === "string" &&
        error.response.data === "itemsInCartNotIncludedInDiscount"
      ) {
        return {
          error: "Please add an included item to your cart",
          action: "rewardAction" as const,
        };
      }
    }
    return {
      error: "Invalid Code. Please enter a valid code",
      action: "rewardAction" as const,
    };
  }
};

export const updateLocation = async (data: UpdateLocationAction) => {
  try {
    await Promise.all([
      await post("/orders/{shopDisplayOrderId}/set-location", {
        shopDisplayOrderId: data.shopDisplayOrderId,
        $data: data.locationId,
      }),
      invalidateCurrentOrderDetails(),
      queryClient.invalidateQueries({
        queryKey: ["/orders/current/lineitems"],
      }),
      queryClient.invalidateQueries({
        queryKey: ["/orders/current/available-promos"],
      }),
      queryClient.invalidateQueries({ queryKey: ["platform/locations"] }),
      queryClient.invalidateQueries({
        queryKey: ["membership/details"],
      }),
    ]);

    if (data.rewardId) {
      await applyReward({
        action: "rewardAction",
        rewardId: data.rewardId,
      });
    }

    return { success: true, action: "updateLocation" as const };
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 400) {
      return { success: false, action: "updateLocation" as const };
    }
  }
};

// replace with this with an API call to the incentive service
export const cartMeetsMinimumSpendRequirementForReward = (
  cartAmount?: number,
  reward?: Models<"RewardModel">
) => {
  if (!reward?.requireMinimumSpendInCart) return true;

  if (!cartAmount) return true;

  return (
    reward?.requireMinimumSpendInCart &&
    cartAmount >= reward?.minimumSpendInCents
  );
};
